import {
    AfterViewInit,
    Component,
    ContentChildren,
    ElementRef,
    EventEmitter,
    Injectable,
    Input,
    OnDestroy,
    Output,
    QueryList,
    ViewChild,
} from '@angular/core';
import { SeStepComponent } from './se-step/se-step.component';
import { FormGroup } from '@angular/forms';
import { BehaviorSubject, combineLatest, Observable, of, Subject, Subscription } from 'rxjs';
import { MatStepper, MatStepperIntl } from '@angular/material/stepper';
import { STEPPER_GLOBAL_OPTIONS, StepperSelectionEvent } from '@angular/cdk/stepper';
import { TooltipPosition } from '@angular/material/tooltip';
import { startWith } from 'rxjs/operators';
import { ActiveStepHookFunction } from '../types';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class StepperIntl extends MatStepperIntl {
    override optionalLabel = 'OptionalStep';
}

/**
 * Questa classe rappresenta un componente stepper custom.
 * Viene utilizzato per navigare attraverso una serie di step e gestire i controlli del form.
 */
@Component({
    selector: 'se-stepper',
    templateUrl: './se-stepper.component.html',
    styleUrls: ['./se-stepper.component.scss'],
    providers: [
        {
            provide: STEPPER_GLOBAL_OPTIONS,
            useValue: { displayDefaultIndicatorType: false, showError: true }, //displayDefaultIndicatorType:false to use custom icons
        }, //https://material.angular.io/cdk/stepper/api set state to get corresponding icons
    ],
    standalone: false,
})
export class SeStepperComponent implements AfterViewInit, OnDestroy {
    @ContentChildren(SeStepComponent, { descendants: true }) seSteps: QueryList<SeStepComponent> =
        undefined as unknown as QueryList<SeStepComponent>;

    @ViewChild('stepper')
    stepper!: MatStepper;

    /**
     * il formGroup può essere passato come input, per gestire dati pre-valorizzati
     * as esempio nel caso di paziente pre-selezionato che deve comunque vedersi nel side
     * o il cui valore deve poter essere letto dagli altri step
     * in tal caso chi utilizza se-stepper puo preconfezionare un formGroup che abbia gia un sotto formGroup con i dati desiderati
     */
    @Input()
    form: FormGroup = new FormGroup({});

    afterInitCompleted = false;
    selectedIndex: number = 0;

    /* INPUTS */
    @Input()
    tooltipHideDelay: number = 5000;
    @Input()
    optionalLabelText: string = 'OptionalStep';
    @Input()
    matTooltipPosition: TooltipPosition = 'after';

    /**
     * il codice di default di questa function era necessario per mettere in errore le icone del mat-step nell'header
     * cosa fattibile solo tramite manipolazione diretta sull'html
     * la funzione viene lasciata come hook per gestire tutte le cablature del casto es classe dell'icone errore, particolari attenzioni ad alcuni step (vedi patient)
     * oppure gestione di altre logiche al cambio del current step specifiche per il contesto di utilizzo
     * @param stepName
     * @param seSteps
     * @param selectedIndex
     * @param elementRef
     */
    @Input()
    currentActiveStepHook: ActiveStepHookFunction = (
        stepName: string,
        seSteps: QueryList<SeStepComponent>,
        selectedIndex: number,
        elementRef: ElementRef<any>,
    ) => {
        const currentStep = seSteps.get(selectedIndex);
        const headerStepIconNativeRef = elementRef.nativeElement.querySelectorAll(
            '.mat-horizontal-stepper-header .mat-step-icon',
        );
        if (currentStep?.hasError || (stepName === 'patient' && !currentStep?.isCompleted)) {
            headerStepIconNativeRef.forEach((native: HTMLElement) => native.classList.add('error-stepper-icon'));
        } else {
            headerStepIconNativeRef.forEach((native: HTMLElement) => native.classList.remove('error-stepper-icon'));
        }
    };

    /**
     * usato per disabilitare l'header dello stepper nel caso in cui si apra una pagina di modifica (es. modifica paziente)
     * dalla quale non è permesso navigare tra gli step
     */
    @Input() disableHeader?: boolean = false;

    /* OUTPUS */
    @Output()
    stepperIsValid: EventEmitter<boolean> = new EventEmitter<boolean>(false);
    @Output()
    currentStep: EventEmitter<number> = new EventEmitter<number>();
    @Output()
    value: EventEmitter<any> = new EventEmitter<any>();
    @Output()
    isCurrentStepValid: EventEmitter<boolean> = new EventEmitter<boolean>(false);

    gotoNextStep$: Subject<string> = new Subject<string>();
    currentActiveStep$: BehaviorSubject<string> = new BehaviorSubject<string>('');
    private headerSubs: Subscription[] = [];
    private selectionSub: Subscription | null = null;

    ngAfterViewInit() {
        this.seSteps.forEach((step, idx) => {
            /* <se-steps> figli sono ready solo nell'afterviewinit,
            solo qui si puo creare ogni sotto formGroup ed aggiungerlo al formGroup di tutto lo step*/
            step.getControlValue = this.getControlValue;
            step.gotoNextStep$ = this.gotoNextStep$;
            step.currentActiveStep$ = this.currentActiveStep$;

            step.getObservableValueFunctions$.next({
                getObservableValue: this.getObservableValue,
                getObservableValueList: this.getObservableValueList,
                getObservableValueMap: this.getObservableValueMap,
            });
            const subForm = new FormGroup({});
            /* il nome del sotto form dipende dal nome assegnato allo step il title potrebbe essere tradotto quindi
             * si usa solo nel template dello step nell'header*/
            this.form.addControl(step.name, subForm);
            step.form$.next(subForm);

            if (step.readOnly) {
                /* unico modo per mettere la classe readonly su mat-step-header
                 * da valutare se il readonly deve essere dinamico trasformarlo in un observable*/
                this.stepper._stepHeader.get(idx)?._elementRef.nativeElement.classList.add('readonly');
            }
        });

        this.setupStepIconErrorHighlighting();

        this.updateOptionalLabel();
        setTimeout(() => (this.afterInitCompleted = true));

        this.form.statusChanges.subscribe(() => {
            this.stepperIsValid.emit(this.form.valid);
            let allValue = this.form.getRawValue(); //considera anche il valore dei control disabilitati
            this.value.emit(allValue);
        });

        this.gotoNextStep$.subscribe((stepName) => {
            /* stepName da usare per applicare altre logiche se necessarie*/
            const callingStepIdx = [...this.seSteps].findIndex((step) => step.name === stepName);
            if (callingStepIdx !== this.selectedIndex) return;
            if (stepName) this.next();
        });

        this.currentActiveStep$.next(this.seSteps.get(this.selectedIndex)?.name ?? '');

        /* on init se il primo step fosse readonly passare al successivo*/
        if (this.seSteps.get(this.selectedIndex)?.readOnly) {
            this.next();
        }

        this.currentActiveStep$.subscribe((stepName) => {
            setTimeout(() => {
                this.currentActiveStepHook(stepName, this.seSteps, this.selectedIndex, this.elementRef);
            }, 100);
        });
    }

    constructor(
        private _matStepperIntl: MatStepperIntl,
        private elementRef: ElementRef,
        private translateService: TranslateService
    ) { }


    ngOnDestroy(): void {
        this.headerSubs.forEach((s) => s.unsubscribe());
        if (this.selectionSub) {
            this.selectionSub.unsubscribe();
        }    }

    /**
     * da usare nel caso servisse modificare il label optional a run time
     */
    updateOptionalLabel() {
        this._matStepperIntl.optionalLabel = this.translateService.instant(this.optionalLabelText);
        this._matStepperIntl.changes.next();
    }

    /**
     * restituisce un observable oppure null se non esiste il path cercato nel form
     * se il path esiste restituisce l'observable valueChanges di quel controllo all'interno del form
     * @param path percorso da cui leggere il valore iniziale nel form in stile binding X.Y.Z
     */
    getObservableValue = (path: string): Observable<any> | null => {
        const control = this.form.get(path.split('.'));
        return control ? control.valueChanges : null;
    };

    /**
     * recupera una lista di osservabili in base ai percorsi specificati e restituisce un osservabile
     * che emette la lista di valori corrispondenti ai path nel form.
     *
     * @param {string[]} paths -una array di stringhe di path nel form.
     * @return {Observable<any[]>}
     */
    getObservableValueList = (paths: string[]): Observable<any[]> => {
        return combineLatest(
            paths.map((path) => this.getObservableValue(path)).map((obs) => (obs ?? of(null)).pipe(startWith(null))),
        );
    };

    /**
     * ritorna un'osservabile di una mappa di valori indicizzati per path.
     * se nel form non viene trovato il path corrispondente si ha valore sempre null.
     * @param paths lista di path corrispondenti a controlli nel form.
     * @returns Una osservabile di una mappa in cui ogni chiave è un percorso nel form con il corrispondete valore attuale.
     */
    getObservableValueMap = (paths: string[]): Observable<Record<string, any>> => {
        const observableMap: Record<string, Observable<any>> = {};

        paths.forEach((path) => {
            const control = this.form.get(path.split('.'));
            observableMap[path] = (control ? control.valueChanges : of(null)).pipe(startWith(null));
        });

        return combineLatest(observableMap);
    };

    /**
     * dato che valueChanges non fornisce il valore iniziale serve un metodo per andarlo a leggere nei controlli che estendono baseAbstract
     * @param path percorso da cui leggere il valore iniziale nel form in stile binding X.Y.Z
     */
    getControlValue = (path: string): any => {
        const control = this.form.get(path.split('.'));
        return control ? control.value : null;
    };

    /**
     * estensione di getControlValue che prende una lista di path in input e ritorna una lista di valori
     * @param paths
     */
    getControlValueList = (paths: string[]): any[] => paths.map((path) => this.getControlValue(path));

    /**
     * Restituisce una mappa dei valori correnti per i percorsi specificati all'interno del form.
     * @param paths An array of paths for which to retrieve current values.
     * @returns A map where each key is a path and each value is the current value for that path.
     */
    getControlValueMap = (paths: string[]): Record<string, any> => {
        return Object.fromEntries(
            paths.map((path) => {
                const control = this.form.get(path.split('.'));
                return [path, control ? control.value : null];
            }),
        );
    };

    /* implementare qualunque logica serva quando l'utente cambia step
     * di default restituisce lo step corrente con emitter */
    handleSelectionChange = ($event: StepperSelectionEvent) => {
        this.currentStep.emit($event.selectedIndex);
        this.currentActiveStep$.next(this.seSteps.get($event.selectedIndex)?.name ?? '');
    };

    canGotoNextStep = (): boolean => {
        const currentStep = this.seSteps.get(this.selectedIndex);
        if (!currentStep) return false;
        this.isCurrentStepValid.emit(!(currentStep.hasError || (!currentStep.isOptional && !currentStep.isCompleted)));
        return !(currentStep.hasError || (!currentStep.isOptional && !currentStep.isCompleted));
    };

    next = (): boolean => {
        const canGotoNext = this.canGotoNextStep();
        if (!canGotoNext) return false;
        this.selectedIndex = this.selectedIndex < this.seSteps.length - 1 ? this.selectedIndex + 1 : this.selectedIndex;
        const nextStep = this.seSteps.get(this.selectedIndex);
        /* salto il prossimo step se readonly*/
        if (nextStep?.readOnly && this.selectedIndex < this.seSteps.length - 1) return this.next();
        return true;
    };

    /* il prev non ha vincoli su completamento / opzionalità
essendo un step precedente di sicuro non ha errori oppure e` completo/opzionale*/
    prev = (): boolean => {
        this.selectedIndex = this.selectedIndex > 0 ? this.selectedIndex - 1 : this.selectedIndex;
        const prevStep = this.seSteps.get(this.selectedIndex);
        /* salto step readonly anche all'indietro*/
        if (this.selectedIndex !== 0 && prevStep?.readOnly) return this.prev();
        return true;
    };

    hasErrors(): boolean {
        // Check for errors in the form
        return this.form.invalid;
    }

    private setupStepIconErrorHighlighting(): void {
        const getIconEl = (idx: number): HTMLElement | null =>
            this.stepper._stepHeader?.get(idx)?._elementRef?.nativeElement
                ?.querySelector('.mat-step-icon') ?? null;
        const applyForStep = (idx: number, step: SeStepComponent) => {
            const iconEl = getIconEl(idx);
            if (!iconEl) return;

            const hasErrors = step.errorsList && step.errorsList.length > 0;
            const isActive = this.selectedIndex === idx;
            const iconClass = 'se-step-icon-active-error';

            if (hasErrors && isActive) {
                iconEl.classList.add(iconClass);
            } else {
                iconEl.classList.remove(iconClass);
            }
        };

        // ogni volta che cambia errorsList di uno step
        this.seSteps.forEach((step, idx) => {
            step.component?.errorsList$.subscribe(() => {
                setTimeout(() => applyForStep(idx, step));
            });
        });

        // ogni volta che cambio step attivo
        this.stepper.selectionChange.subscribe(() => {
            setTimeout(() => {
                this.seSteps.forEach((step, idx) => applyForStep(idx, step));
            });
        });
    }

}
